import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  alpha,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import { motion } from 'framer-motion';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import SendIcon from '@mui/icons-material/Send';
import PhoneIcon from '@mui/icons-material/Phone';
import { useTenantConfig } from '../hooks/useTenant';

const ApplyPage = () => {
  const {tenantName} = useTenantConfig();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeStep, setActiveStep] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', tcno: '', birthDate: '',
    companyName: '', taxNumber: '', companyAddress: '', companyPhone: '', companyEmail: '',
    services: [], package: '', usage: '',
    kvkk: false, terms: false, marketing: false,
  });

  const steps = ['Kişisel Bilgiler', 'Kurum Bilgileri', 'Hizmet Seçimi', 'Onay'];

  const services = [
    { id: 'widget', name: 'Widget Sistemi', desc: 'Sürükle-bırak sayfa tasarımı' },
    { id: 'mobile', name: 'Mobil Uygulama', desc: 'iOS & Android erişim' },
    { id: 'payment', name: 'Ödeme Sistemi', desc: 'Aidat & bağış toplama' },
  ];

  const packages = [
    { id: 'baslangic', name: 'Başlangıç', price: '₺299/ay' },
    { id: 'profesyonel', name: 'Profesyonel', price: '₺599/ay' },
    { id: 'kurumsal', name: 'Kurumsal', price: '₺1.299/ay' },
  ];

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleServiceToggle = (id) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(id)
        ? prev.services.filter(s => s !== id)
        : [...prev.services, id],
    }));
  };

  const handleNext = () => setActiveStep(prev => prev + 1);
  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setActiveStep(0);
      setFormData({
        firstName: '', lastName: '', email: '', phone: '', tcno: '', birthDate: '',
        companyName: '', taxNumber: '', companyAddress: '', companyPhone: '', companyEmail: '',
        services: [], package: '', usage: '',
        kvkk: false, terms: false, marketing: false,
      });
    }, 5000);
  };

  const renderStep = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#0f172a' }}>
              Kişisel Bilgiler
            </Typography>
            <Grid container spacing={2}>
              {['firstName', 'lastName', 'email', 'phone', 'tcno'].map((field, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <TextField
                    fullWidth
                    label={field === 'firstName' ? 'Ad' : field === 'lastName' ? 'Soyad' : field === 'email' ? 'E-posta' : field === 'phone' ? 'Telefon' : 'TC Kimlik No'}
                    name={field}
                    value={formData[field]}
                    onChange={handleChange}
                    required
                    size="small"
                    type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                  />
                </Grid>
              ))}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Doğum Tarihi"
                  name="birthDate"
                  type="date"
                  value={formData.birthDate}
                  onChange={handleChange}
                  required
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </>
        );

      case 1:
        return (
          <>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#0f172a' }}>
              Dernek Bilgileri
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}><TextField fullWidth label="Dernek Adı" name="companyName" value={formData.companyName} onChange={handleChange} required size="small" /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Vergi No" name="taxNumber" value={formData.taxNumber} onChange={handleChange} required size="small" /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Telefon" name="companyPhone" value={formData.companyPhone} onChange={handleChange} size="small" /></Grid>
              <Grid item xs={12}><TextField fullWidth label="E-posta" name="companyEmail" value={formData.companyEmail} onChange={handleChange} type="email" size="small" /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Adres" name="companyAddress" value={formData.companyAddress} onChange={handleChange} multiline rows={2} required size="small" /></Grid>
            </Grid>
          </>
        );

      case 2:
        return (
          <>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#0f172a' }}>
              Hizmet & Paket Seçimi
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
              İhtiyacınıza uygun hizmetleri seçin.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {services.map(s => (
                <Grid item xs={12} sm={4} key={s.id}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      border: formData.services.includes(s.id) ? '2px solid #f97316' : '1px solid #e5e7eb',
                      backgroundColor: formData.services.includes(s.id) ? '#fff7ed' : '#fff',
                      transition: 'all 0.3s ease',
                      '&:hover': { borderColor: '#f97316', transform: 'translateY(-4px)' },
                    }}
                    onClick={() => handleServiceToggle(s.id)}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <CheckCircleIcon sx={{ fontSize: 36, color: formData.services.includes(s.id) ? '#f97316' : '#9ca3af', mb: 1 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>{s.desc}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Paket</InputLabel>
              <Select name="package" value={formData.package} onChange={handleChange} label="Paket">
                {packages.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.name} - {p.price}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Kullanım Amacı"
              name="usage"
              value={formData.usage}
              onChange={handleChange}
              multiline
              rows={2}
              size="small"
              placeholder="Örn: Aidat toplama, etkinlik duyurusu..."
            />
          </>
        );

      case 3:
        return (
          <>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#0f172a' }}>
              Onay & Gönder
            </Typography>
            <Box sx={{
              display:'flex',
              flexDirection:'column'
            }}>
              <FormControlLabel
              control={<Checkbox checked={formData.kvkk} onChange={handleChange} name="kvkk" required sx={{ color: '#f97316', '&.Mui-checked': { color: '#f97316' } }} />}
              label={<Typography variant="body2">KVKK metnini okudum, kabul ediyorum.</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={formData.terms} onChange={handleChange} name="terms" required sx={{ color: '#f97316', '&.Mui-checked': { color: '#f97316' } }} />}
              label={<Typography variant="body2">Kullanım şartlarını kabul ediyorum.</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={formData.marketing} onChange={handleChange} name="marketing" sx={{ color: '#f97316', '&.Mui-checked': { color: '#f97316' } }} />}
              label={<Typography variant="body2">Kampanya e-postaları almak istiyorum.</Typography>}
            />
            </Box>
          </>
        );

      default: return null;
    }
  };

  return (
    <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      {/* HERO - FİYATLANDIRMA GİBİ */}
      <Box sx={{ pt: { xs: 14, md: 18 }, backgroundColor: '#ffffff' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', }}>
            <Typography
              variant="overline"
              sx={{
                color: '#6b7280',
                fontWeight: 600,
                letterSpacing: '3px',
                fontSize: '0.85rem',
                mb: 2,
              }}
            >
              BAŞVURU
            </Typography>

            <Typography
              variant="h2"
              sx={{
                fontSize: isMobile ? '2.4rem' : '3.2rem',
                fontWeight: 900,
                mb: 3,
                color: '#0f172a',
                letterSpacing: '-0.02em',
              }}
            >
              Derneğinizi
              <Box component="span" sx={{ color: '#f97316' }}> Dijitalleştirin</Box>
            </Typography>

            <Typography
              variant="h6"
              sx={{
                maxWidth: '720px',
                mx: 'auto',
                color: '#6b7280',
                lineHeight: 1.8,
                fontSize: isMobile ? '1rem' : '1.15rem',
              }}
            >
              4 adımda {tenantName}.com’a üye olun. Hemen başlayın.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Success Message */}
      {showSuccess && (
        <Container maxWidth="lg" sx={{ mt: -6, mb: 6, position: 'relative', zIndex: 10 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                p: 4,
                background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                borderRadius: 3,
                textAlign: 'center',
                border: '2px solid #6ee7b7',
                boxShadow: '0 8px 24px rgba(16, 185, 129, 0.15)',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 48, color: '#059669', mb: 1 }} />
              <Typography variant="h5" sx={{ color: '#065f46', fontWeight: 700, mb: 1 }}>
                Başvurunuz Başarıyla Alındı!
              </Typography>
              <Typography variant="body1" sx={{ color: '#047857' }}>
                Teşekkür ederiz. Ekibimiz en kısa sürede size dönüş yapacaktır.
              </Typography>
            </Box>
          </motion.div>
        </Container>
      )}

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Stepper */}
        <Card
          elevation={0}
          sx={{
            mb: 4,
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, i) => (
                <Step key={i} completed={activeStep > i}>
                  <StepLabel
                    StepIconProps={{
                      style: {
                        color: activeStep > i ? '#f97316' : activeStep === i ? '#f97316' : '#cbd5e1',
                        fontSize: '2rem',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: activeStep === i ? 700 : 500,
                        color: activeStep >= i ? '#1e293b' : '#94a3b8',
                        fontSize: { xs: '0.8rem', md: '0.9rem' },
                        mt: 1,
                      }}
                    >
                      {label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        {/* Form */}
        <motion.div
          key={activeStep}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <Card
            elevation={0}
            sx={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <form onSubmit={handleSubmit}>
                {renderStep(activeStep)}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5, gap: 2 }}>
                  <Button
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    variant="outlined"
                    sx={{
                      borderColor: '#cbd5e1',
                      color: '#64748b',
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#94a3b8',
                        backgroundColor: alpha('#64748b', 0.04),
                      },
                      '&.Mui-disabled': {
                        borderColor: '#e2e8f0',
                        color: '#cbd5e1',
                      },
                    }}
                  >
                    Geri
                  </Button>
                  {activeStep === steps.length - 1 ? (
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<SendIcon />}
                      disabled={!formData.kvkk || !formData.terms}
                      sx={{
                        background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 700,
                        boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                          boxShadow: '0 6px 16px rgba(249, 115, 22, 0.4)',
                        },
                        '&.Mui-disabled': {
                          background: '#e2e8f0',
                          color: '#94a3b8',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Başvuruyu Gönder
                    </Button>
                  ) : (
                    <Button
                      onClick={handleNext}
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 700,
                        boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                          boxShadow: '0 6px 16px rgba(249, 115, 22, 0.4)',
                        },
                      }}
                    >
                      İleri
                    </Button>
                  )}
                </Box>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Özellik Kartları */}
        <Grid container spacing={3} sx={{ mt: 4 }}>
          {[
            { icon: <PersonIcon sx={{ fontSize: 32 }} />, title: 'Kolay Kullanım', desc: 'Sürükle-bırak arayüz ile hızlı işlem' },
            { icon: <SecurityIcon sx={{ fontSize: 32 }} />, title: 'Güvenli', desc: 'KVKK & GDPR uyumlu altyapı' },
            { icon: <CheckCircleIcon sx={{ fontSize: 32 }} />, title: 'Hızlı Kurulum', desc: '7 gün içinde sisteminiz hazır' },
          ].map((item, i) => (
            <Grid item xs={12} md={4} key={i}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                whileHover={{ y: -4 }}
              >
                <Card
                  elevation={0}
                  sx={{
                    textAlign: 'center',
                    p: 4,
                    height: '100%',
                    borderRadius: 3,
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: alpha('#f97316', 0.3),
                      boxShadow: `0 8px 24px ${alpha('#f97316', 0.12)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
                    {item.desc}
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Yardım Merkezi */}
        <Card
          elevation={0}
          sx={{
            mt: 6,
            p: 4,
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderRadius: 3,
            border: '1px solid #e2e8f0',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
            Yardıma mı ihtiyacınız var?
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
            Size yardımcı olmak için burada yız. Sorınuz veya destek talebiniz varsa bize ulaşın.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PhoneIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                  color: 'white',
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                  },
                }}
              >
                Canlı Destek
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                sx={{
                  borderColor: '#f97316',
                  color: '#f97316',
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#ea580c',
                    backgroundColor: alpha('#f97316', 0.04),
                  },
                }}
              >
                0850 255 19 06
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                sx={{
                  borderColor: '#cbd5e1',
                  color: '#64748b',
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#94a3b8',
                    backgroundColor: alpha('#64748b', 0.04),
                  },
                }}
              >
                SSS
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                sx={{
                  borderColor: '#cbd5e1',
                  color: '#64748b',
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#94a3b8',
                    backgroundColor: alpha('#64748b', 0.04),
                  },
                }}
              >
                Rehber
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Container>
    </Box>
  );
};

export default ApplyPage;